<template>
    <section class="pb-8 pr-1 sm:pr-6 content-top">
        <Head :title="metaTitle + ' Challenges'" />

        <heading class="mx-2 lg:mx-0">
            <template #heading>
                <span class="capitalize">{{ list }} Challenges</span>
            </template>
        </heading>

        <div class="flex justify-center mx-2 sm:justify-start lg:mx-0">
            <div class="justify-content-center relative inline-flex mb-4 p-0.5 bg-gray-200 rounded-lg space-x-2 sm:mx-0">
                <Link
                    :href="route('posts.published')"
                    :class="[route().current('posts.published') ? 'shadow-sm border-gray-200 bg-white text-gray-900 pointer-events-none' : 'bg-transparent border-transparent text-gray-500 hover:text-gray-800 hover:bg-gray-50 hover:bg-opacity-30']"
                    class="focus:z-10 px-8 py-2 w-1/2 whitespace-nowrap text-xs font-medium border rounded-md focus:outline-none uppercase focus:ring-2 focus:ring-indigo-500 sm:w-auto"
                    >Published
                </Link>
                <Link
                    :href="route('accepted.index')"
                    :class="[route().current('accepted.index') ? 'shadow-sm border-gray-200 bg-white text-gray-900 pointer-events-none' : 'bg-transparent border-transparent text-gray-500 hover:text-gray-800 hover:bg-gray-50 hover:bg-opacity-30']"
                    class="focus:z-10 ml-0.5 px-8 py-2 w-1/2 whitespace-nowrap text-xs font-medium border rounded-md focus:outline-none uppercase focus:ring-2 focus:ring-indigo-500 sm:w-auto"
                    >Accepted
                </Link>
            </div>
        </div>

        <div class="bg-white shadow overflow-hidden lg:rounded-lg">
            <div class="bg-white overflow-hidden sm:grid sm:gap-0 sm:grid-cols-2">
                <div v-if="post.featuredImageHtml" v-for="post in posts" class="group relative hover:bg-gray-50 bg-white odd:border-r border-gray-300 transition duration-100 ease-in-out yellow-ring-500">
                    <div class="relative">
                        <div class="aspect-w-10 aspect-h-7 block p-0 w-full h-48 shadow-inner transition duration-100 ease-in-out group-hover:brightness-90 group-hover:contrast-125 filter sm:overflow-hidden" v-html="post.featuredImageHtml"></div>
                        <div v-if="post.competitor" class="absolute top-0 left-0 bg-green-500 text-xl font-bold text-white px-6 py-3 rounded-br-lg shadow">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" class="feather-check inline-block h-6 w-6 mr-1">
                                <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            Completed
                        </div>
                        <div v-if="post.pendingCompetitors && post.pendingCompetitors.length" class="absolute bg-opacity-80 bg-white bottom-0 font-semibold gap-x-2 inline-flex items-center left-0 px-4 py-3 right-0 text-sm text-yellow-800 uppercase">
                            <span class="font-medium gap-x-1.5 inline-flex items-center px-2 py-1 rounded-md text-xs text-yellow-700">
                                <span class="flex h-4 relative w-4">
                                    <span class="absolute animate-ping bg-yellow-600 h-full inline-flex opacity-75 rounded-full w-full"></span>
                                    <span class="bg-yellow-600 h-4 inline-flex relative rounded-full w-4"></span>
                                </span>
                            </span>
                            {{ post.pendingCompetitors.length }} Pending {{ "Competitor" | pluralize(post.pendingCompetitors.length) }}
                        </div>
                    </div>
                    <div class="flex flex-col items-center mb-4 mx-2 w-full sm:mb-0 lg:mx-0">
                        <Link :href="post.pendingCompetitors.length ? route('challengeRequests.index', { post: post }) : post.showUrl" class="focus:outline-none">
                            <!-- Extend touch target to entire panel -->
                            <span class="absolute inset-0" aria-hidden="true"></span>
                        </Link>
                        <div class="flex flex-row items-center justify-between mx-4 space-x-4 sm:mx-8">
                            <div class="group flex flex-col items-center px-2 text-pink-500 font-display text-2xl font-bold sm:left-5 sm:top-5 sm:px-5 lg:text-3xl xl:text-5xl">
                                <span class="currency inline-flex leading-none">{{ post.pledgedAmount | abbr }}</span>
                                <!--<span class="currency inline-flex leading-none">{{ post.pledgedAmount | abbr }}</span>-->
                            </div>
                            <!-- Post Details -->
                            <div class="flex flex-col my-4 min-w-0 gap-2">
                                <p class="line-clamp-2 hover:text-gray-600 text-gray-700 text-xs xl:text-sm" v-html="post.text"></p>
                                <div class="hover:text-gray-600 text-gray-500 text-sm">
                                    Published
                                    <span class="has-tooltip cursor-help">
                                        <time :datetime="post.publishedAt">{{ post.publishedAt | when }}</time>
                                        <tooltip>Published {{ post.publishedAt | dateFormat("LLL") }}</tooltip>
                                    </span>
                                </div>
                                <div class="hidden gap-2 items-center group-hover:text-gray-600 text-gray-500 text-sm xl:flex">
                                    <div class="text-center">{{ post.pledgesCount | abbr }} pledges</div>

                                    <svg class="shrink-0 w-5 h-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                                        <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                                    </svg>

                                    <div class="text-center">{{ post.commentsCount | abbr }} comments</div>

                                    <svg class="hidden flex-shrink-0 w-5 h-5 text-gray-300 xl:block" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                                        <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                                    </svg>

                                    <div class="text-center">{{ post.likesCount | abbr }} likes</div>

                                    <svg class="hidden flex-shrink-0 w-5 h-5 text-gray-300 xl:block" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                                        <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                                    </svg>

                                    <span v-if="!post.isApproved" class="has-tooltip cursor-help">
                                        <time :datetime="post.expiresAt">Expires {{ post.expiresAt | daysDifference }}</time>
                                        <tooltip>Expires on {{ post.expiresAt | dateFormat("LLL") }}</tooltip>
                                    </span>
                                    <span v-if="post.isApproved" class="font-bold">
                                        <svg class="w-5 h-5 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M5 13l4 4L19 7" stroke-width="2.5"></path>
                                        </svg>
                                        Completed
                                    </span>

                                    <span v-if="list === 'expired'">
                                        Expired
                                        <time :datetime="post.expiredAt">{{ post.expiredAt | when }}</time>
                                    </span>
                                    <div class="text-center" v-if="list === 'pending'">
                                        Saved
                                        <time :datetime="post.createdAt">{{ post.createdAt | when }}</time>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- < sm -->
                        <div class="flex gap-1 items-center place-content-center group-hover:text-gray-500 text-gray-500 text-sm sm:hidden">
                            <span>{{ post.pledges_count | abbr }} pledges</span>

                            <svg class="flex-shrink-0 w-5 h-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                                <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                            </svg>

                            <span>{{ post.commentsCount | abbr }} comments</span>

                            <svg class="flex-shrink-0 w-5 h-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                                <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                            </svg>

                            <span>{{ post.likesCount | abbr }} likes</span>

                            <svg class="flex-shrink-0 w-5 h-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                                <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                            </svg>

                            <span class="has-tooltip cursor-help" v-if="list === 'published'">
                                <time :datetime="post.publishedAt">{{ post.publishedAt | when }}</time>
                                <tooltip>Published {{ post.publishedAt | dateFormat("LLL") }}</tooltip>
                            </span>
                            <span v-if="list === 'expired'">
                                Expired
                                <time :datetime="post.expiredAt">{{ post.expiredAt | when }}</time>
                            </span>
                            <span v-if="list === 'pending'">
                                Saved
                                <time :datetime="post.createdAt">{{ post.createdAt | when }}</time>
                            </span>
                        </div>
                    </div>
                    <span class="absolute right-6 top-5 text-gray-300 group-hover:text-white pointer-events-none drop-shadow backdrop-filter" aria-hidden="true">
                        <svg class="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M20 4h1a1 1 0 00-1-1v1zm-1 12a1 1 0 102 0h-2zM8 3a1 1 0 000 2V3zM3.293 19.293a1 1 0 101.414 1.414l-1.414-1.414zM19 4v12h2V4h-2zm1-1H8v2h12V3zm-.707.293l-16 16 1.414 1.414 16-16-1.414-1.414z" />
                        </svg>
                    </span>
                </div>
                <div v-if="posts.length && posts.length < 4" class="last:border-t border-transparent sm:border-gray-300"></div>
            </div>

            <no-results :show="!posts.length">
                <span class="capitalize">No {{ list }} Challenges</span>
            </no-results>

            <!-- Pagination -->
            <pagination :data="paginate" :only="['paginate']" label="challenges" />
        </div>

        <!-- Delete Post Modal -->
        <delete-post-modal :post.sync="deletingPost"></delete-post-modal>
    </section>
</template>

<script>
import AppLayout from "@/Layouts/AppLayout"
import NoResults from "@/Jetstream/NoResults"
import Pagination from "@/Components/Pagination"
import JetDropdown from "@/Jetstream/Dropdown"
import JetDropdownLink from "@/Jetstream/DropdownLink"
import Heading from "@/Components/Heading"
import DeletePostModal from "@/Pages/Post/DeletePostModal"
import JetButton from "@/Jetstream/Button"
import AlertWarning from "@/Components/Alerts/Warning"
import Tooltip from "@/Components/Tooltip"
import SubmitChallengeButton from "@/Pages/Post/UploadChallengeButton"

export default {
    name: "List",

    components: {
        SubmitChallengeButton,
        Tooltip,

        AlertWarning,
        JetButton,
        DeletePostModal,
        Heading,
        Pagination,
        NoResults,
        JetDropdown,
        JetDropdownLink,
    },

    layout: AppLayout,

    props: ["paginate", "counts", "sort"],

    computed: {
        metaTitle() {
            return _.capitalize(this.list)
        },

        posts() {
            return this.paginate.data
        },

        list() {
            return this.paginate.list
        },
    },

    watch: {
        currentTab(tab) {
            this.$inertia.get(this.route("user.posts.list", tab))
        },
    },

    data() {
        return {
            deletingPost: null,
            listNames: ["published", "pending", "expired"],
            currentTab: this.list,
        }
    },

    methods: {
        changeTab(event) {
            this.$inertia.get(event.target.value)
        },

        confirmDeletePost(post) {
            this.deletingPost = post
        },
    },
}
</script>
